import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { format, isSameDay } from 'date-fns';

import { addDays } from 'date-fns/esm';
import {
  collection,
  query,
  where,
  getDocs,
  // deleteDoc,
  doc,
  orderBy,
  limit,
  startAfter,
  updateDoc,
  getDoc,
} from 'firebase/firestore';
import POS from '~/pages/POS';

// import Swal from 'sweetalert2';

import Dashboard from '~/layouts/Dashboard';
import { useAuth } from '~/hooks/auth';
// import { useToast } from '~/hooks/toast';
import { ORDER_STATUS, ORDER_TYPES } from '~/utils/getEnums';
import { useOrders } from '~/hooks/orders';
import IOrder from '~/interfaces/IOrder';
import { useToast } from '~/hooks/toast';

import { DB } from '~/services/firebase';
import getCurrencyFormatted from '~/utils/getCurrencyFormatted';
import IClient from '~/interfaces/IClient';

const OrdersOnCredit: React.FC = () => {
  const { addToast } = useToast();
  const { user } = useAuth();
  const { openPos, setOpenPos } = useOrders();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('Fiados de Hoje');
  const [showFilter, setShowFilter] = useState(false);
  const [perPage] = useState(50);
  const [last, setLast] = useState({});
  const [orders, setOrders] = useState<IOrder[]>([]);

  useEffect(() => {
    async function load() {
      const ordersRef = collection(DB, 'orders');
      const q = query(
        ordersRef,
        where('company_user_id', '==', user.uid),
        where('paymentMethod', '==', '4'),
        orderBy('created_at', 'desc'),
        limit(perPage),
      );

      const querySnapshot = await getDocs(q);

      const lastItem = querySnapshot.docs[querySnapshot.docs.length - 1];
      setLast(lastItem);

      const items: IOrder[] = [];

      querySnapshot.docs.forEach((item: any) => {
        const data = item.data() as IOrder;
        const created_at = new Date(data.created_at.seconds * 1000);
        let sell_on_credit_date = data.sell_on_credit_date
          ? new Date(data.sell_on_credit_date)
          : new Date();
        sell_on_credit_date = addDays(sell_on_credit_date, 1);

        const parseItem: IOrder = {
          ...data,
          uid: item.id,
          shippingCost_formatted: getCurrencyFormatted(data.shippingCost),
          subtotal_formatted: getCurrencyFormatted(data.subtotal),
          total_formatted: getCurrencyFormatted(data.total),
          created_at_formatted: format(created_at, 'dd/MM/yyyy HH:mm'),
          sell_on_credit_date_formatted: format(
            sell_on_credit_date,
            'dd/MM/yyyy',
          ),
        };

        items.push(parseItem);
      });

      setOrders(items);
    }

    if (user.uid) load();
  }, [user, perPage]);

  const filteredOrders = useMemo(() => {
    const filtered = orders.filter(order => {
      let result = true;

      if (status === 'Fiados de Hoje') {
        const today = new Date();
        let payment_date = new Date(order.sell_on_credit_date || '');
        payment_date = addDays(payment_date, 1);

        const isSameDate = isSameDay(today, payment_date);

        if (order.code) {
          const orderName = order.name
            .toLocaleLowerCase()
            .includes(search.toLocaleLowerCase());

          const orderCode = order.code
            .toLocaleLowerCase()
            .includes(search.toLocaleLowerCase());

          result = (orderName || orderCode) && isSameDate;
        } else {
          const orderName = order.name
            .toLocaleLowerCase()
            .includes(search.toLocaleLowerCase());

          result = orderName && isSameDate;
        }
      } else if (order.code) {
        result =
          order.name.toLocaleLowerCase().includes(search.toLocaleLowerCase()) ||
          order.code.toLocaleLowerCase().includes(search.toLocaleLowerCase());
      } else {
        result = order.name
          .toLocaleLowerCase()
          .includes(search.toLocaleLowerCase());
      }

      return result;
    });

    return filtered;
  }, [search, orders, status]);

  const updateCustomer = useCallback(
    async (client_id: string, value: number) => {
      if (client_id) {
        const docSnap = await getDoc(doc(DB, 'clients', client_id));
        const item = docSnap.data() as IClient;
        const client = {
          ...item,
          uid: client_id,
        } as IClient;

        if (client.buy_on_credit >= 0) client.buy_on_credit -= value;

        await updateDoc(doc(DB, 'clients', client_id), {
          ...client,
        });
      }
    },
    [],
  );

  const handlePay = useCallback(
    async (uid: string) => {
      const items = [...orders];

      const findIndex = items.findIndex(item => item.uid === uid);

      if (findIndex !== -1) {
        const orderUpdated: IOrder = {
          ...items[findIndex],
          credit_payed: true,
        };

        if (orderUpdated.uid) {
          await updateDoc(doc(DB, 'orders', orderUpdated.uid), {
            credit_payed: true,
          });

          await updateCustomer(orderUpdated.client_id, orderUpdated.total);

          items[findIndex] = orderUpdated;

          setOrders(items);

          addToast({
            type: 'success',
            description: 'Fiado pago com sucesso!',
          });
        }
      }
    },
    [orders, addToast, updateCustomer],
  );

  return (
    <Dashboard containerMessage="col-xl-10 offset-xl-1">
      <div className="col-xl-10 offset-xl-1">
        <div className="nk-block-head nk-block-head-sm">
          <div className="nk-block-between">
            <div className="nk-block-head-content">
              <h3 className="nk-block-title page-title">Fiados</h3>
            </div>
            <div className="nk-block-head-content">
              <div className="toggle-wrap nk-block-tools-toggle">
                <button
                  type="button"
                  className={`btn btn-icon btn-trigger toggle-expand mr-n1 ${
                    showFilter && 'active'
                  }`}
                  data-target="pageMenu"
                  onClick={() => setShowFilter(current => !current)}
                >
                  <em className="icon ni ni-more-v" />
                </button>
                <div
                  className={`toggle-expand-content ${
                    showFilter && 'expanded'
                  }`}
                  data-content="pageMenu"
                >
                  <ul className="nk-block-tools g-3">
                    <li>
                      <div className="form-control-wrap">
                        <div className="form-icon form-icon-right">
                          <em className="icon ni ni-search" />
                        </div>
                        <input
                          type="text"
                          className="form-control"
                          id="default-04"
                          placeholder="Buscar por código ou cliente"
                          onChange={event => {
                            setSearch(event.target.value);
                          }}
                        />
                      </div>
                    </li>
                    <li>
                      <div className="drodown">
                        <Link
                          to="#status"
                          className="dropdown-toggle dropdown-indicator btn btn-outline-light btn-white"
                          data-toggle="dropdown"
                        >
                          {status}
                        </Link>
                        <div className="dropdown-menu dropdown-menu-right">
                          <ul className="link-list-opt no-bdr">
                            <li>
                              <Link
                                to="#all"
                                onClick={() => {
                                  setStatus('Todos');
                                  setShowFilter(false);
                                }}
                              >
                                <span>Todos</span>
                              </Link>
                            </li>
                            <li>
                              <Link
                                to="#today"
                                onClick={() => {
                                  setStatus('Fiados de Hoje');
                                  setShowFilter(false);
                                }}
                              >
                                <span>Fiados de Hoje</span>
                              </Link>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row tb-card">
          {filteredOrders.length === 0 && (
            <div className="col-md-12">
              <div className="card card-bordered">
                <div className="card-inner">
                  <div className="d-flex justify-content-center">
                    Você não possui pedidos.
                  </div>
                </div>
              </div>
            </div>
          )}

          {filteredOrders.map(item => (
            <div className="col-md-6 col-sm-6 mb-3" key={item.uid}>
              <div className="card card-bordered">
                <div className="card-inner">
                  <div className="team">
                    <Link
                      to={`/orders/details/${item.uid}`}
                      style={{ color: '#000', textTransform: 'none' }}
                    >
                      <div className="user-card user-card-s2">
                        {/* <div className="user-avatar lg bg-primary">
                        <span>{getInitialLettersOfName(item.name)}</span>
                        <div className="status dot dot-lg dot-success" />
                      </div> */}
                        <div>
                          <h6 className="mb-1">{item.name}</h6>
                          <span className="sub-text mb-2">{item.whatsapp}</span>

                          {item.order_type !== undefined &&
                            !item.table?.uid && (
                              <h6>{ORDER_TYPES[item.order_type]}</h6>
                            )}
                          {item.order_type === '2' && item.table !== null && (
                            <h6>
                              Mesa:
                              {item.table?.name}
                            </h6>
                          )}
                        </div>
                      </div>

                      <ul className="team-info-custom">
                        <li>
                          <span>Pedido</span>
                          <span>{`#${item.code}`}</span>
                        </li>
                        <li>
                          <span>Data e Hora</span>
                          <span className="text-right">
                            {item.created_at_formatted}
                          </span>
                        </li>
                        <li>
                          <span>Total</span>
                          <span>{item.total_formatted}</span>
                        </li>
                        <li>
                          <span>Status</span>
                          {item.status === '0' && (
                            <span className="badge badge-dot badge-primary">
                              {ORDER_STATUS[item.status]}
                            </span>
                          )}
                          {item.status === '1' && (
                            <span className="badge badge-dot badge-warning">
                              {ORDER_STATUS[item.status]}
                            </span>
                          )}
                          {item.status === '2' && (
                            <span className="badge badge-dot badge-info">
                              {ORDER_STATUS[item.status]}
                            </span>
                          )}
                          {item.status === '3' && (
                            <span className="badge badge-dot badge-success">
                              {ORDER_STATUS[item.status]}
                            </span>
                          )}
                          {item.status === '4' && (
                            <span className="badge badge-dot badge-danger">
                              {ORDER_STATUS[item.status]}
                            </span>
                          )}
                        </li>
                      </ul>
                    </Link>
                    <div className="team-view">
                      <button
                        type="button"
                        className="btn btn-icon btn-white btn-dim btn-sm btn-primary ms-2 ps-2 pe-2"
                        onClick={() => handlePay(item.uid || '')}
                      >
                        <em className="icon ni ni-printer-fill" />
                        QUITAR
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {user && user.uid && (
            <button className="page-link" type="button" style={{ border: 0 }}>
              <em className="icon ni ni-chevrons-right mr-1" />
              Carregar mais
            </button>
          )}
        </div>

        <div className="nk-block tb-status">
          <div className="card card-bordered">
            <div className="card-inner-group">
              <div className="card-inner p-0">
                <table className="table table-orders">
                  <thead className="tb-odr-head">
                    <tr className="tb-odr-item">
                      <th className="tb-odr-info tb-code">Código</th>
                      <th className="tb-odr-info tb-client">Cliente</th>
                      <th className="tb-odr-info tb-total">Total</th>
                      <th className="tb-odr-info tb-total">Data Pagamento</th>
                      <th className="tb-odr-info tb-total">Pagamento</th>
                      <th className="tb-odr-action" style={{ width: 225 }}>
                        &nbsp;
                      </th>
                    </tr>
                  </thead>
                  <tbody className="tb-odr-body">
                    {filteredOrders.map(order => (
                      <tr className="tb-odr-item" key={order.uid}>
                        <td>
                          <Link to={`/orders/details/${order.uid}`}>
                            {`#${order.code}`}
                          </Link>
                          <br />
                          {order.created_at_formatted}
                        </td>
                        <td>
                          <strong style={{ textTransform: 'capitalize' }}>
                            {order.name}
                          </strong>
                          {order.order_type !== undefined && !order.table?.uid && (
                            <span>
                              <br />
                              {ORDER_TYPES[order.order_type]}
                            </span>
                          )}
                          {order.order_type === '2' && order.table !== null && (
                            <span>
                              <br />
                              {`Mesa: ${order.table?.name}`}
                            </span>
                          )}
                        </td>
                        <td>{order.total_formatted}</td>
                        <td>{order.sell_on_credit_date_formatted}</td>
                        <td>
                          {!order.credit_payed && (
                            <p className="text-danger">PENDENTE</p>
                          )}
                          {order.credit_payed && (
                            <p className="text-success">PAGO</p>
                          )}
                        </td>

                        <td className="tb-odr-action" style={{ minWidth: 255 }}>
                          {!order.credit_payed && (
                            <button
                              type="button"
                              className="btn btn-icon btn-white btn-dim btn-sm btn-primary pe-2"
                              onClick={() => handlePay(order.uid || '')}
                            >
                              <em className="icon ni ni-check-round" />
                              QUITAR
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="card-inner">
                <div className="nk-block-between-md g-3">
                  <div className="g">
                    <ul className="pagination justify-content-center justify-content-md-start">
                      <li className="page-item">
                        <button className="page-link" type="button">
                          <em className="icon ni ni-chevrons-right mr-1" />
                          Carregar mais
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => setOpenPos(true)}
        style={{
          width: 42,
          height: 42,
          borderRadius: '50%',
          background: '#000',
          color: '#fff',
          position: 'fixed',
          bottom: 10,
          right: 10,
          zIndex: 9,
        }}
      >
        <em className="icon ni ni-plus" />
      </button>

      {openPos && (
        <>
          <POS />
          <button
            onClick={() => setOpenPos(false)}
            type="button"
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              zIndex: 999999,
              width: 38,
              height: 38,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <em className="icon fa fa-times" />
          </button>
        </>
      )}
    </Dashboard>
  );
};

export default OrdersOnCredit;
